/**
 * Wall-clock time in a named zone, converted to an instant.
 *
 * A `datetime-local` input yields "2026-10-05T14:30" — a wall-clock reading with
 * no zone at all. `new Date(value)` interprets it in the *browser's* zone, which
 * is not the studio's: an owner in Moscow scheduling for a Vladivostok studio
 * would book seven hours out, and every part of the system downstream would
 * agree with the wrong answer. The studio's own timezone is the authority
 * (`tenants.timezone`), so the conversion has to name it.
 */

interface WallClock {
  readonly year: number
  readonly month: number
  readonly day: number
  readonly hour: number
  readonly minute: number
}

/** Parses `YYYY-MM-DDTHH:mm`, the value a `datetime-local` input produces. */
export function parseWallClock(value: string): WallClock | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value)
  if (match === null) return null

  const [, year, month, day, hour, minute] = match
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined
  ) {
    return null
  }

  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
  }
}

/**
 * The zone's offset from UTC at a given instant, in minutes.
 *
 * Derived by formatting the instant in that zone and reading the wall clock
 * back: the difference between that reading treated as UTC and the instant
 * itself *is* the offset. This avoids shipping a timezone database.
 */
function offsetMinutesAt(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)

  const read = (type: Intl.DateTimeFormatPartTypes): number => {
    const value = parts.find((part) => part.type === type)?.value
    return value === undefined ? 0 : Number(value)
  }

  // `hour: '2-digit'` with hour12: false can render midnight as 24.
  const hour = read('hour') % 24

  const asUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    hour,
    read('minute'),
    read('second'),
  )

  return (asUtc - instant.getTime()) / 60_000
}

/**
 * Converts a wall-clock reading in `timeZone` to an ISO instant.
 *
 * Two passes, because the offset depends on the instant we are still solving
 * for: the first guess can land on the wrong side of a DST change, and
 * re-deriving the offset at the candidate corrects it. Russia keeps a fixed
 * offset today, so the second pass is usually a no-op — it is here so this stays
 * correct for a studio in a zone that does change.
 *
 * Returns `null` for an unparseable value or an unknown zone, rather than a
 * plausible-looking wrong instant.
 */
export function wallClockToIso(value: string, timeZone: string): string | null {
  const clock = parseWallClock(value)
  if (clock === null) return null

  const asUtc = Date.UTC(clock.year, clock.month - 1, clock.day, clock.hour, clock.minute)
  if (Number.isNaN(asUtc)) return null

  let offset: number
  try {
    offset = offsetMinutesAt(new Date(asUtc), timeZone)
  } catch {
    return null
  }

  let instant = asUtc - offset * 60_000
  const corrected = offsetMinutesAt(new Date(instant), timeZone)
  if (corrected !== offset) instant = asUtc - corrected * 60_000

  const result = new Date(instant)
  if (Number.isNaN(result.getTime())) return null
  return result.toISOString()
}

/** The studio's wall clock for an instant, for confirming what was chosen. */
export function formatInZone(iso: string, timeZone: string): string {
  const instant = new Date(iso)
  if (Number.isNaN(instant.getTime())) return ''

  return new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(instant)
}

export interface ZonedMoment {
  /** 0 = Sunday, as `Date.getDay()` and `tenant_working_hours.weekday`. */
  readonly weekday: number
  /** Minutes since the studio's midnight. */
  readonly minutes: number
  /** The studio's calendar date, `YYYY-MM-DD`. */
  readonly isoDate: string
}

const WEEKDAY_INDEX: Readonly<Record<string, number>> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

/**
 * The studio's wall clock at an instant.
 *
 * `Date.getDay()`, `getHours()` and friends answer in the *browser's* zone.
 * For a customer whose phone is set elsewhere — travelling, or near a zone
 * border — every "is it open now", "which day is today" and "what time is this
 * slot" computed that way is off by the difference, and nothing downstream can
 * tell. This answers in the studio's zone instead.
 */
export function momentInZone(instant: Date, timeZone: string): ZonedMoment {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant)

  const read = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? ''

  return {
    weekday: WEEKDAY_INDEX[read('weekday')] ?? 0,
    minutes: (Number(read('hour')) % 24) * 60 + Number(read('minute')),
    isoDate: `${read('year')}-${read('month')}-${read('day')}`,
  }
}

/** `HH:mm` on the studio's clock. */
export function timeInZone(instant: Date | string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(instant))
}

/**
 * The instant the studio's day begins, `offsetDays` after the studio's today.
 *
 * "Today" and "this week" in the cabinet are the studio's calendar. Taking
 * midnight from `new Date(y, m, d)` gave the owner's browser's midnight, so an
 * owner away from the studio saw a day that started and ended hours off —
 * bookings near midnight fell into the wrong day or out of the list.
 */
export function studioDayStart(now: Date, timeZone: string, offsetDays = 0): string | null {
  const [year, month, day] = momentInZone(now, timeZone).isoDate.split('-').map(Number)
  const shifted = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + offsetDays))
  return wallClockToIso(`${shifted.toISOString().slice(0, 10)}T00:00`, timeZone)
}
